/**
 * Bulk decisions on the Trade Desk (CIO request 2026-11-04).
 *
 * The CIO declined fourteen Sweet-Spot trim tickets one at a time, with the same reason, on
 * the morning this was asked for. Bulk select lets one reason cover a group of tickets.
 *
 * WHAT IT IS NOT: a batch write. Each selected ticket is posted to the SAME per-ticket
 * disposition endpoint, one after another, with the same body the single-ticket dialog sends.
 * So every ticket still gets its own TradeAudit, TradeLogs and ledger row naming who decided,
 * the server's "already dispositioned" 409 still guards each one, and a failure on one ticket
 * cannot undo or block another. Nothing is sent to a broker; every order is placed in TWS.
 *
 * WHAT STAYS ONE AT A TIME (CIO 2026-11-04: "all four, traded = sells only"):
 *  - "I traded this" on a BUY. An option buy must say how many filled (the trimmable-lot
 *    minimum is judged on the fill), and the server can ask the lot-minimum question.
 *  - "I traded this" on a home-run harvest. The 60-day redeployment lockout is struck on the
 *    contracts filled and the average price, which only the single dialog asks for.
 * Decline, Not yet and Cancel need nothing but a reason, so they bulk for any ticket.
 */
import { describeDispositionHarvestLock } from "./harvestLockout";

export type BulkDecision = "executed" | "cancelled" | "declined" | "deferred";

export type BulkTicket = {
  orderId: number;
  action?: string | null;
  reason?: string | null;
};

/* Mirror of HelionBackend strategy/harvest_triggers.py, which the disposition handler uses
   to decide that an EXECUTED record starts the 60-day lockout: an exact machine token, or the
   watchlist's human phrase anywhere in a prose reason, case-insensitive. The server stays the
   authority -- a harvest recorded without its fill is refused there with a 400 carrying
   `requires`/`trigger`, before anything is written -- so this mirror only keeps such tickets out
   of the bulk list in the first place. */
export const HARVEST_TOKENS: readonly string[] = ["home_run_trim", "cohort_home_run_trim"];
export const HARVEST_PHRASES: readonly string[] = ["winner worth harvesting"];

export function isHarvestReason(reason?: string | null): boolean {
  const text = String(reason ?? "").trim().toLowerCase();
  if (!text) return false;
  if (HARVEST_TOKENS.includes(text)) return true;
  return HARVEST_PHRASES.some((p) => text.includes(p));
}

/** Why this ticket cannot take this decision in bulk, or null when it can. */
export function bulkExclusion(t: BulkTicket, decision: BulkDecision): string | null {
  if (decision !== "executed") return null;
  const action = String(t.action ?? "").trim().toUpperCase();
  if (action === "BUY") return "Buy: record it on its own, with the contracts filled.";
  if (isHarvestReason(t.reason)) {
    return "Home-run harvest: record it on its own; the 60-day lockout needs its fill and price.";
  }
  // SELL is the one action bulk "I traded this" is for. Anything else (blank, or a value
  // this page has not seen) is left to the single dialog rather than guessed at.
  if (action !== "SELL") return `Action "${t.action || "none"}": record it on its own.`;
  return null;
}

export function splitForBulk<T extends BulkTicket>(
  tickets: T[],
  decision: BulkDecision
): { eligible: T[]; excluded: { ticket: T; why: string }[] } {
  const eligible: T[] = [];
  const excluded: { ticket: T; why: string }[] = [];
  for (const t of tickets) {
    const why = bulkExclusion(t, decision);
    if (why) excluded.push({ ticket: t, why });
    else eligible.push(t);
  }
  return { eligible, excluded };
}

/** The request body for one ticket: the single dialog's body with every optional figure blank. */
export function bulkBody(decision: BulkDecision, why: string, actor: string, revisitOn?: string) {
  return {
    decision,
    notes: why,
    actor,
    ...(decision === "deferred" && revisitOn ? { revisitOn } : {}),
  };
}

export type BulkOutcomeKind =
  | "recorded" // written, ticket retired (or, for a deferral, left live and noted)
  | "already" // 409: the ticket was already decided; nothing written
  | "needs_single" // 422, or the harvest 400: only the single dialog can answer it; nothing written
  | "recorded_with_problem" // written, but part of the record did not land -- never hidden
  | "failed" // not written (or unknown); the ticket stays selected for another try
  | "not_attempted"; // stopped before this ticket was sent

export type BulkOutcome = { orderId: number; kind: BulkOutcomeKind; text: string };

/** One disposition response, in words. Mirrors the single dialog's branches. */
export function classifyResponse(orderId: number, status: number, ok: boolean, json: any): BulkOutcome {
  const err = typeof json?.error === "string" && json.error.trim() ? json.error.trim() : "";
  if (status === 409) {
    return { orderId, kind: "already", text: err || "Already decided; nothing was written." };
  }
  // 422 is the lot-minimum question; a 400 naming what it `requires` (or the harvest `trigger`)
  // is the home-run harvest refusal. Both need figures only the single dialog collects, so
  // trying the same bulk request again can never succeed.
  const asksForFigures = status === 400 && (Array.isArray(json?.requires) || typeof json?.trigger === "string");
  if (status === 422 || asksForFigures) {
    return {
      orderId,
      kind: "needs_single",
      text: `Not recorded: ${/[.!?]$/.test(err) ? err : `${err || "the server needs more detail"}.`} Record this one on its own.`,
    };
  }
  if (!ok) return { orderId, kind: "failed", text: `Not recorded: ${err || `HTTP ${status}`}.` };
  // A 2xx that does not say it succeeded (an empty or HTML body from a proxy, say) is not
  // evidence of a write. The ticket stays selected; a retry of a retired ticket answers 409.
  if (json?.status !== "success") {
    return {
      orderId,
      kind: "failed",
      text: `The server answered HTTP ${status} without confirming the record. Check the ledger before trying again.`,
    };
  }

  // Recorded. These two are the single dialog's "recorded, but" cases, shown the same way.
  const lock = describeDispositionHarvestLock(json?.harvestLock);
  if (lock.tone === "error") {
    return { orderId, kind: "recorded_with_problem", text: `Recorded, but ${lock.text}` };
  }
  if (json?.ledgerRecorded === false) {
    return {
      orderId,
      kind: "recorded_with_problem",
      text: "Recorded in the audit trail, but NOT written to the ledger (ledger table unavailable). Tell an engineer before trading further.",
    };
  }
  // Any other lockout notice (started, already running, full exit, book unreadable) is
  // carried through rather than collapsed to a bare "Recorded."
  return { orderId, kind: "recorded", text: lock.text ? `Recorded. ${lock.text}` : "Recorded." };
}

/** Outcomes that mean "this ticket is done with": dropped from the selection after a run. */
export const SETTLED_KINDS: readonly BulkOutcomeKind[] = ["recorded", "already", "recorded_with_problem"];

export function countOutcomes(outcomes: BulkOutcome[]): Record<BulkOutcomeKind, number> {
  const n: Record<BulkOutcomeKind, number> = {
    recorded: 0,
    already: 0,
    needs_single: 0,
    recorded_with_problem: 0,
    failed: 0,
    not_attempted: 0,
  };
  for (const o of outcomes) n[o.kind] += 1;
  return n;
}

const VERB: Record<BulkDecision, string> = {
  executed: "recorded as traded",
  declined: "declined",
  deferred: "deferred",
  cancelled: "cancelled",
};

/** One line for the page banner after a run. Problems first; never a bare "done". */
export function summarizeOutcomes(
  decision: BulkDecision,
  outcomes: BulkOutcome[],
  /** Selected tickets this decision could not take in bulk ("I traded these" takes plain sells only). */
  leftOut = 0
): { tone: "success" | "warning" | "error"; text: string } {
  const n = countOutcomes(outcomes);
  const total = outcomes.length;
  const parts: string[] = [`${n.recorded + n.recorded_with_problem} of ${total} ${VERB[decision]}`];
  if (n.recorded_with_problem) parts.push(`${n.recorded_with_problem} recorded with a problem (see the list)`);
  const one = (k: number, single: string, plural: string) => (k === 1 ? single : plural);
  if (n.failed) parts.push(`${n.failed} failed and ${one(n.failed, "stays", "stay")} selected`);
  if (n.needs_single) parts.push(`${n.needs_single} ${one(n.needs_single, "needs recording on its own", "need recording on their own")}`);
  if (n.already) parts.push(`${n.already} ${one(n.already, "was", "were")} already decided`);
  if (n.not_attempted) parts.push(`${n.not_attempted} not sent (stopped)`);
  if (leftOut) parts.push(`${leftOut} left out (record ${one(leftOut, "it on its", "them on their")} own)`);
  const tone =
    n.recorded_with_problem || n.failed ? "error" : n.needs_single || n.not_attempted || leftOut ? "warning" : "success";
  return { tone, text: `${parts.join("; ")}.` };
}

/** Open tickets that share a system reason, for one-click "select all with this reason". */
export function reasonGroups<T extends BulkTicket>(tickets: T[]): { reason: string; ids: number[] }[] {
  const by = new Map<string, number[]>();
  for (const t of tickets) {
    const r = String(t.reason ?? "").trim();
    if (!r) continue;
    const ids = by.get(r) ?? [];
    ids.push(t.orderId);
    by.set(r, ids);
  }
  return Array.from(by.entries())
    .filter(([, ids]) => ids.length >= 2)
    .map(([reason, ids]) => ({ reason, ids }))
    .sort((a, b) => b.ids.length - a.ids.length || a.reason.localeCompare(b.reason));
}
